import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import { CheckCircleIcon } from "@phosphor-icons/react/CheckCircle";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { Divider } from "@freightsimple/ui-core/ComponentLayout/Divider";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { PaymentsSummaryBox } from "../CustomComponents/PaymentsSummaryBox";
import { PaymentsSummaryItem } from "../CustomComponents/PaymentsSummaryItem";
import type { InvoiceSummary } from "../Types/paymentsPaymentTypes";

interface PaymentsConfirmMakePaymentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  confirmText: string;
  totalAmountToPay: number;
  currency: string;
  selectedPaymentMethod: ReactNode;
  invoiceSummary: InvoiceSummary;
  /** What confirming does to the money — charged now vs. acknowledged as sent. */
  paymentMethodText: string;
}

/**
 * The last look before the money moves: the total, the method, and what
 * confirming means, with the real charge amounts one more time.
 */
export function PaymentsConfirmMakePaymentsDialog({
  open,
  onOpenChange,
  onConfirm,
  confirmText,
  totalAmountToPay,
  currency,
  selectedPaymentMethod,
  invoiceSummary,
  paymentMethodText,
}: PaymentsConfirmMakePaymentsDialogProps) {
  // Guard against double-clicks: a second tap would otherwise fire onConfirm
  // twice and trigger two charges for the same invoices. A ref because it has
  // to block synchronous repeat clicks, which land before any re-render.
  const hasConfirmedRef = useRef(false);

  // The dialog stays mounted between opens, so the guard re-arms on each open.
  useEffect(() => {
    if (open) {
      hasConfirmedRef.current = false;
    }
  }, [open]);

  const handleConfirmPayment = () => {
    if (hasConfirmedRef.current) {
      return;
    }
    hasConfirmedRef.current = true;
    onOpenChange(false);
    onConfirm();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="narrow"
      title="Confirm Payment"
      description={paymentMethodText}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            primary
            icon={CheckCircleIcon}
            onClick={handleConfirmPayment}
            data-testid="confirm-make-payments"
          >
            {confirmText}
          </Button>
        </>
      }
    >
      <Stack gap={5} align="stretch">
        <Stack align="center" gap={0}>
          <T size="lg" weight="medium" color="muted">
            TOTAL:
          </T>
          <Row gap={1}>
            <T size="2xl" weight="medium" color="secondary" nowrap>
              ${totalAmountToPay}
            </T>
            <T size="2xl" weight="medium" color="muted" nowrap>
              {currency}
            </T>
          </Row>
        </Stack>

        <PaymentsSummaryBox>
          <PaymentsSummaryItem>
            Payment Method: {selectedPaymentMethod}
          </PaymentsSummaryItem>

          {invoiceSummary.shipment.count > 0 && (
            <>
              <Divider spacing="sm" />
              <PaymentsSummaryItem>
                Shipment Charge ({invoiceSummary.shipment.count}):{" "}
                <T weight="medium" color="secondary" nowrap>
                  ${invoiceSummary.shipment.total.toFixed(2)}
                </T>
              </PaymentsSummaryItem>
            </>
          )}

          {invoiceSummary.additional.count > 0 && (
            <>
              <Divider spacing="sm" />
              <PaymentsSummaryItem>
                Additional Charge ({invoiceSummary.additional.count}):{" "}
                <T weight="medium" color="secondary" nowrap>
                  ${invoiceSummary.additional.total.toFixed(2)}
                </T>
              </PaymentsSummaryItem>
            </>
          )}
        </PaymentsSummaryBox>
      </Stack>
    </Dialog>
  );
}
